import ListFilters from '@/common/components/ListFilters'
import Table from '@/common/components/Table'
import TextField from '@/common/components/TextField'
import { useEquipmentManufacturers } from '@/common/hooks/queries/useEquipmentManufacturers'
import useDebounce from '@/common/hooks/useDebounce'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from '@/common/services/mapper'
import { EquipmentManufacturersData } from '@/common/types/equipmentManufacturers'
import { useEquipmentModels } from '@/features/equipmentModels/hooks/useEquipmentModels'
import { EquipmentModelData } from '@/features/equipmentModels/types/equipmentModels'
import { homologatedColumns } from '@/features/modelIntegrationCodes/constants/columns.constant'
import { homologatedStatusOptions } from '@/features/modelIntegrationCodes/constants/homologatedStatusOptions.constant'
import { useHomologatedModelIntegrationCodes } from '@/features/modelIntegrationCodes/hooks/useHomologatedModelIntegrationCodes'
import { ModelIntegrationCodesTransformer } from '@/features/modelIntegrationCodes/services/modelIntegrationCodesTransformer'
import {
  HomologatedIntegrationStatus,
  HomologatedModelIntegrationCode,
  HomologatedModelIntegrationCodeTableItem,
} from '@/features/modelIntegrationCodes/types/modelIntegrationCode.type'
import { SearchIcon } from 'lucide-react'

const HomologatedList = () => {
  const { filterParams, setFilterParams } = useFilterParams()
  const [search, setSearch] = useDebounce(filterParams.search || '')
  const [manufacturerSearch, setManufacturerSearch] = useDebounce('')
  const [equipmentModelSearch, setEquipmentModelSearch] = useDebounce('')

  const { data: manufacturerOptions = [] } = useEquipmentManufacturers({
    filters: { search: manufacturerSearch },
    mapper: Mapper.mapToOptions<EquipmentManufacturersData>({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  })

  const { data: equipmentModelOptions = [] } = useEquipmentModels({
    filters: { search: equipmentModelSearch },
    mapper: Mapper.mapToOptions<EquipmentModelData>({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  })

  const {
    data: items = [],
    meta,
    isPending,
    isFetching,
  } = useHomologatedModelIntegrationCodes<HomologatedModelIntegrationCodeTableItem[]>({
    filters: {
      search,
      manufacturer_id: filterParams.manufacturer?.value,
      integration_status: filterParams.status?.value as HomologatedIntegrationStatus | '' | undefined,
      equipment_model_id: filterParams.equipment_model?.value,
      page: Number(filterParams.page) || 1,
    },
    mapper: Mapper.mapWithTransformer<HomologatedModelIntegrationCode[], HomologatedModelIntegrationCodeTableItem[]>({
      transformer: ModelIntegrationCodesTransformer.homologatedTableTransformer(),
    }),
  })

  return (
    <div className="flex flex-col gap-4">
      <TextField
        LeftIcon={<SearchIcon />}
        placeholder="Busque por nome do modelo"
        className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
        onChange={(event) => {
          const value = (event.target as HTMLInputElement).value
          setSearch(value)
          setFilterParams((prev) => ({ ...prev, search: value, page: 1 }))
        }}
        value={filterParams.search || ''}
        dataTestid="homologated_model_integration_codes_search"
      />

      <ListFilters
        filters={[
          {
            placeholder: 'Fabricante',
            options: [{ label: 'Todos', value: '' }, ...manufacturerOptions],
            value: filterParams.manufacturer || null,
            onChange: (selected) => setFilterParams((prev) => ({ ...prev, manufacturer: selected, page: 1 })),
            onInputChange: setManufacturerSearch,
            dataTestid: 'manufacturer_filter',
            operationKlassToCheckPermission: 'Admin::EquipmentModels::IntegrationCodesFilter',
          },
          {
            placeholder: 'Status',
            options: [{ label: 'Todos', value: '' }, ...homologatedStatusOptions],
            value: filterParams.status || null,
            onChange: (selected) => setFilterParams((prev) => ({ ...prev, status: selected, page: 1 })),
            dataTestid: 'status_filter',
            operationKlassToCheckPermission: 'Admin::EquipmentModels::IntegrationCodesFilter',
          },
          {
            placeholder: 'Modelo do Equipamento',
            options: [{ label: 'Todos', value: '' }, ...equipmentModelOptions],
            value: filterParams.equipment_model || null,
            onChange: (selected) => setFilterParams((prev) => ({ ...prev, equipment_model: selected, page: 1 })),
            onInputChange: setEquipmentModelSearch,
            dataTestid: 'equipment_model_filter',
            operationKlassToCheckPermission: 'Admin::EquipmentModels::IntegrationCodesFilter',
          },
        ]}
      />

      <Table
        columns={homologatedColumns}
        rows={items}
        isLoadingData={isPending || isFetching}
        page={Number(filterParams.page) || 1}
        totalPages={meta?.total_pages || 1}
        totalCount={meta?.total || 0}
        hasPagination
        setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
        rowEmptyValue="--"
      />
    </div>
  )
}

export default HomologatedList
